import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { authClient, signOut } from "./auth-client";
import { useAuth } from "./AuthContext";
import "./Auth.css";

export default function SetupMfa() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, mfaEnabled } = useAuth();

  const [password, setPassword] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // 'password', 'scan', 'verify', 'complete'

  const autoStarted = useRef(false);

  // If MFA is already enabled, go straight to dashboard
  useEffect(() => {
    if (mfaEnabled) {
      navigate("/dashboard", { replace: true });
    }
  }, [mfaEnabled, navigate]);

  // Auto-start 2FA when arriving from registration (password is in router state)
  useEffect(() => {
    const state = location.state;
    if (state?.fromRegistration && state?.password && !autoStarted.current) {
      autoStarted.current = true;
      setPassword(state.password);
      // Clear the sensitive state from history
      window.history.replaceState({}, document.title);
      enable2FA(state.password);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const enable2FA = async (pwd) => {
    setLoading(true);
    setError("");

    try {
      const result = await authClient.twoFactor.enable({ password: pwd });

      if (result.error) {
        setError(result.error.message || "Failed to enable 2FA. Check your password.");
        setStep("password");
        setLoading(false);
        return;
      }

      const d = result.data;
      if (d) {
        const totpUri = d.totpURI || d.totpUri || d.totp_uri || d.qrCode || "";
        const secretData = d.secret || d.totpSecret || "";
        const codes = d.backupCodes || [];

        setQrCode(totpUri);
        setSecret(secretData);
        setBackupCodes(codes);

        if (!totpUri && !secretData) {
          setError("Could not generate 2FA credentials. Please try again.");
          setStep("password");
          setLoading(false);
          return;
        }

        setStep("scan");
        setSuccess("Scan the QR code with your authenticator app");
      } else {
        setError("No response from server. Please try again.");
        setStep("password");
      }
    } catch (err) {
      console.error("2FA enable error:", err);
      setError("Failed to setup 2FA: " + (err.message || "Unknown error"));
      setStep("password");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    await enable2FA(password);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await authClient.twoFactor.verifyTotp({
        code: verificationCode,
      });

      if (result.error) {
        setError(result.error.message || "Invalid code. Please try again.");
        setVerificationCode("");
        setLoading(false);
        return;
      }

      setStep("complete");
      setSuccess("Account secured with 2FA! Redirecting to dashboard...");
      setTimeout(() => navigate("/dashboard", { replace: true }), 2500);
    } catch (err) {
      setError(err.message || "Verification failed");
      setVerificationCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  // Determine if we're coming from registration (show different header)
  const fromRegistration = location.state?.fromRegistration || step !== "password";

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Step 1: Confirm password (shown only when NOT from registration) */}
        {step === "password" && (
          <>
            <div className="auth-header">
              <h2>Setup Required</h2>
              <p>
                Hi {user?.name || "there"}, two-factor authentication is
                mandatory. Please set it up to continue.
              </p>
              <div className="step-indicator">
                <span className="step active">1. Confirm Password</span>
                <span className="step">2. Scan QR</span>
                <span className="step">3. Verify</span>
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit} className="auth-form">
              {error && (
                <div className="error-message">
                  <span className="error-icon">&#9888;&#65039;</span>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="mfa-password">Confirm Your Password</label>
                <input
                  id="mfa-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="form-input"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || !password}
                className={`auth-button ${loading ? "loading" : ""}`}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Setting up...
                  </>
                ) : (
                  "Continue to 2FA Setup"
                )}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                <button
                  onClick={handleLogout}
                  className="auth-link"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Sign out and go back
                </button>
              </p>
            </div>
          </>
        )}

        {/* Loading state while generating QR */}
        {step === "password" && loading && (
          <div className="auth-form" style={{ marginTop: "20px" }}>
            <div className="loading-message">
              <span className="spinner"></span>
              <p>Generating your 2FA credentials...</p>
            </div>
          </div>
        )}

        {/* Step 2: Scan QR code */}
        {step === "scan" && (
          <>
            <div className="auth-header">
              <h2>Scan QR Code</h2>
              <p>Use Google Authenticator, Authy, or any TOTP app</p>
              <div className="step-indicator">
                {fromRegistration ? (
                  <span className="step completed">&#10003; Account Created</span>
                ) : (
                  <span className="step completed">&#10003; Password</span>
                )}
                <span className="step active">2. Scan QR</span>
                <span className="step">3. Verify</span>
              </div>
            </div>

            <div className="auth-form">
              {loading && (
                <div className="loading-message">
                  <span className="spinner"></span>
                  <p>Generating your 2FA credentials...</p>
                </div>
              )}

              {!loading && success && (
                <div className="success-message">
                  <span className="success-icon">&#10003;</span>
                  {success}
                </div>
              )}

              {!loading && qrCode && (
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <div style={{
                    display: "inline-block",
                    padding: "16px",
                    background: "white",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}>
                    <QRCodeSVG
                      value={qrCode}
                      size={200}
                      level="M"
                    />
                  </div>
                  {secret && (
                    <div
                      style={{
                        marginTop: "15px",
                        padding: "10px",
                        background: "#f7fafc",
                        borderRadius: "8px",
                      }}
                    >
                      <small style={{ color: "#4a5568" }}>
                        <strong>Manual Entry Key:</strong>
                      </small>
                      <code
                        style={{
                          display: "block",
                          marginTop: "5px",
                          padding: "8px",
                          background: "white",
                          borderRadius: "4px",
                          fontSize: "12px",
                          wordBreak: "break-all",
                        }}
                      >
                        {secret}
                      </code>
                    </div>
                  )}
                </div>
              )}

              {!loading && !qrCode && secret && (
                <div
                  style={{
                    padding: "15px",
                    background: "#f7fafc",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <small style={{ color: "#4a5568" }}>
                    <strong>Manual Entry Key (enter in your authenticator app):</strong>
                  </small>
                  <code
                    style={{
                      display: "block",
                      marginTop: "5px",
                      padding: "8px",
                      background: "white",
                      borderRadius: "4px",
                      fontSize: "14px",
                      wordBreak: "break-all",
                      textAlign: "center",
                    }}
                  >
                    {secret}
                  </code>
                </div>
              )}

              {!loading && (
                <button
                  onClick={() => {
                    setStep("verify");
                    setSuccess("");
                    setError("");
                  }}
                  className="auth-button"
                >
                  I've scanned the code — Next
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 3: Verify code */}
        {step === "verify" && (
          <>
            <div className="auth-header">
              <h2>Verify Code</h2>
              <p>Enter the 6-digit code from your authenticator app</p>
              <div className="step-indicator">
                {fromRegistration ? (
                  <span className="step completed">&#10003; Account Created</span>
                ) : (
                  <span className="step completed">&#10003; Password</span>
                )}
                <span className="step completed">&#10003; Scan QR</span>
                <span className="step active">3. Verify</span>
              </div>
            </div>

            <form onSubmit={handleVerify} className="auth-form">
              {error && (
                <div className="error-message">
                  <span className="error-icon">&#9888;&#65039;</span>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="verificationCode">6-Digit Code</label>
                <input
                  id="verificationCode"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  required
                  className="form-input otp-input"
                  disabled={loading}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className={`auth-button ${loading ? "loading" : ""}`}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Verifying...
                  </>
                ) : (
                  "Verify & Complete Setup"
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep("scan");
                  setError("");
                }}
                className="toggle-code-type"
                style={{ alignSelf: "center" }}
                disabled={loading}
              >
                Go back to QR code
              </button>
            </form>
          </>
        )}

        {/* Step 4: Complete */}
        {step === "complete" && (
          <>
            <div className="auth-header">
              <h2>Setup Complete!</h2>
              <p>Your account is now secured</p>
            </div>

            <div className="auth-form">
              {success && (
                <div className="success-message">
                  <span className="success-icon">&#10003;</span>
                  {success}
                </div>
              )}

              {backupCodes.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  <div
                    style={{
                      padding: "15px",
                      background: "#fffbeb",
                      borderRadius: "8px",
                      border: "1px solid #f59e0b",
                      marginBottom: "15px",
                    }}
                  >
                    <p
                      style={{
                        margin: "0",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#92400e",
                      }}
                    >
                      Save these backup codes now!
                    </p>
                    <small style={{ color: "#78350f" }}>
                      Use them if you lose access to your authenticator app
                    </small>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "10px",
                    }}
                  >
                    {backupCodes.map((code, index) => (
                      <code
                        key={index}
                        style={{
                          padding: "8px",
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
